import { useState } from "react";
import { motion } from "framer-motion";
import { api } from "../lib/api";
import { RippleButton } from "./RippleButton";
import { toast } from "sonner";
import { Phone, MessageCircle, Instagram, Facebook, Mail, MapPin, Building } from "lucide-react";

const CONTACTS = [
  { icon: Phone, label: "Phone", value: "+91 84211 14134", href: "tel:+918421114134" },
  { icon: MessageCircle, label: "WhatsApp", value: "+91 84211 14134", href: "https://wa.me/918421114134" },
  { icon: Instagram, label: "Instagram", value: "@heywoofsy", href: "https://instagram.com/heywoofsy" },
  { icon: Facebook, label: "Facebook", value: "Woofsy", href: "https://www.facebook.com/share/19FNxr946x/?mibextid=wwXIfr" },
  { icon: Mail, label: "Email", value: "info@woofsy.co.in", href: "mailto:info@woofsy.co.in" },
  { icon: MapPin, label: "Location", value: "Pune, India", href: "https://maps.google.com" },
];

export default function Footer({ onBook }) {
  const [email, setEmail] = useState("");
  const subscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    try { await api.post("/newsletter", { email }); toast.success("Subscribed! Treats incoming 🦴"); setEmail(""); }
    catch { toast.error("Could not subscribe"); }
  };
  return (
    <footer id="contact" data-testid="footer" className="relative">
      {/* Contact */}
      <section className="py-24 px-5 sm:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <p className="text-xs uppercase tracking-[0.2em] font-semibold text-woofsy-primary mb-3">Get In Touch</p>
            <h2 className="font-heading font-bold text-3xl sm:text-5xl text-woofsy-dark">We're always wagging to help.</h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {CONTACTS.map((c, i) => (
              <motion.a key={c.label} href={c.href} target="_blank" rel="noreferrer" data-testid={`contact-${c.label.toLowerCase()}`}
                initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: (i % 3) * 0.1 }}
                whileHover={{ y: -4 }}
                className={`glass rounded-2xl p-5 flex items-center gap-4 hover:shadow-lg transition-shadow ${c.label === "Address" ? "sm:col-span-2 lg:col-span-3" : ""}`}>
                <div className="w-11 h-11 rounded-xl bg-woofsy-primary/15 flex items-center justify-center shrink-0"><c.icon className="w-5 h-5 text-woofsy-primary" /></div>
                <div><p className="text-xs text-woofsy-dark/50">{c.label}</p><p className="font-medium text-woofsy-dark">{c.value}</p></div>
              </motion.a>
            ))}
          </div>
        </div>
      </section>

      {/* Footer bar */}
      <div className="bg-woofsy-dark text-white px-5 sm:px-8 py-12 sm:py-16 rounded-t-[3rem]">
        <div className="max-w-7xl mx-auto grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-8 sm:gap-12">
          <div>
            <div className="mb-3">
              <img src="/logo.png" alt="Woofsy Logo" className="h-20 sm:h-24 max-h-24 w-auto object-contain bg-white/95 p-2 rounded-2xl shadow-md" />
            </div>
            <p className="text-woofsy-primary font-semibold text-sm mb-3">Less Waiting . More Wagging</p>
            <p className="text-white/60 max-w-xs text-sm">Premium at-home pet grooming, delivered to your door in 30 minutes.</p>
            <RippleButton variant="primary" className="mt-6 px-6 py-3 text-sm" data-testid="footer-book-btn" onClick={onBook}>Book Grooming</RippleButton>
          </div>
          <div>
            <h4 className="font-heading font-semibold mb-4 text-woofsy-secondary">Company</h4>
            <ul className="space-y-2 text-white/60 text-sm">
              <li><a href="/about" className="hover:text-woofsy-primary transition-colors">About Us</a></li>
              <li><a href="/promise" className="hover:text-woofsy-primary transition-colors">Our Promise</a></li>
              {["How It Works", "Services", "Why Woofsy", "Reviews", "FAQ"].map((l) => <li key={l}><a href={`/#${l.split(" ")[0].toLowerCase()}`} className="hover:text-woofsy-primary transition-colors">{l}</a></li>)}
            </ul>
          </div>
          <div>
            <h4 className="font-heading font-semibold mb-4 text-woofsy-secondary">Newsletter</h4>
            <p className="text-white/60 text-sm mb-4">Grooming tips & offers for your furry friend.</p>
            <form onSubmit={subscribe} className="flex flex-col sm:flex-row gap-2" data-testid="newsletter-form">
              <input data-testid="newsletter-input" value={email} onChange={(e) => setEmail(e.target.value)} type="email" placeholder="Your email"
                className="flex-1 rounded-full bg-white/10 border border-white/20 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-woofsy-primary placeholder:text-white/40" />
              <RippleButton type="submit" variant="primary" data-testid="newsletter-submit" className="px-5 py-2.5 text-sm">Join</RippleButton>
            </form>
          </div>
        </div>
        <div className="max-w-7xl mx-auto mt-10 sm:mt-14 pt-6 border-t border-white/10 flex flex-col sm:flex-row sm:flex-wrap justify-between gap-3 sm:gap-4 text-white/40 text-xs sm:text-sm">
          <span>© 2026 Woofsy. Made for happy tails.</span>
          <div className="flex items-center gap-4 sm:gap-6">
            <a href="/privacy" className="hover:text-woofsy-primary transition-colors">Privacy Policy</a>
            <a href="/terms" className="hover:text-woofsy-primary transition-colors">Terms & Conditions</a>
            <a href="/admin/login" data-testid="admin-link" className="hover:text-woofsy-primary transition-colors">Admin</a>
          </div>
        </div>
      </div>
    </footer>
  );
}
